import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import has from 'lodash/has';
import pick from 'lodash/pick';

import useTracking from 'components/Globals/Analytics';
import {
  ACTION_CALLBACK_STATUS,
  ACTION_CALLBACK_STEPS,
  COMPONENTS,
  SECTIONS,
} from 'components/Globals/Analytics/constants';

import { LOGIN_SOURCE, PAYWALL_ACTIONS, PAYWALL_EVENT_TYPES, STEP_VALUES, STEPS_LOGIN } from 'constants/index';
import {
  PAYMENT_CALLBACK_STATUSES,
  SUBSCRIPTIONS_STATUSES,
  SUBSCRIPTION_PAYMENT_STATUSES,
} from 'constants/subscriptions';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import {
  useProfileAccesses,
  useUserData,
  useUserSubscriptions,
  useUserPermissions,
  useAccessPendingProfileIds,
} from 'utils/hooks/useAuthenticatedUser';
import { useDialogs } from 'utils/hooks/useDialogs';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';

import { ENTITY_TYPES, PAGE_CATEGORIES, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

// eslint-disable-next-line import/no-cycle
import PaywallContent from './PaywallContent';

const PaywallContext = createContext({
  showSubscriptionPlans: {},
  setShowSubscriptionPlans: () => null,
  isPaywallExpanded: false,
  setIsPaywallExpanded: () => null,
  isReturningGuestUser: false,
});

const Paywall = ({ children }) => {
  const navigate = useNavigate();
  const { permissions, pathname, edit, entity: pageEntity, queryParams } = usePageContext();
  const { refetch: refetchPermissions } = useUserPermissions();
  const { isLoggedIn, paywall, obRouteContext, visitorIdentity, isReturningGuestUser } = useAppContext();
  const { query, page } = obRouteContext;
  const paywallType = paywall?.paywallType;
  const track = useTracking();
  const { isLoginDialog, setPaymentOptionsModal, setIsLoginDialog } = useDialogs();
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState({
    show: false,
    entityType: null,
    entity: null,
    subscription: null,
    identifier: null,
  });
  const {
    data: { data: allProfilesAccesses },
  } = useProfileAccesses();
  const accessPendingProfileIds = useAccessPendingProfileIds();
  const userData = useUserData();
  const { data: userSubscriptions } = useUserSubscriptions();
  const [isPaywallExpanded, setIsPaywallExpandedState] = useState(
    () =>
      showSubscriptionPlans.show ||
      (paywall?.action === PAYWALL_ACTIONS.SHOW && !!paywallType) ||
      query?.m === 'paywall' ||
      false,
  );

  const setIsPaywallExpanded = useCallback(
    value => {
      setIsPaywallExpandedState(value);

      if (isLoginDialog?.isOpen && !value) {
        setIsLoginDialog({ isOpen: false });
      }
    },
    [isLoginDialog?.isOpen, setIsLoginDialog],
  );

  const isOnEntityStubPage = page?.category === PAGE_CATEGORIES.STUB && page?.entity?.id;
  const { isAdmin, isPermissionsReady } = permissions || {};
  const hasEditPermission =
    isOnEntityStubPage &&
    (permissions.artistPermissions?.canEdit ||
      permissions.agencyPermissions?.canEdit ||
      permissions.organizationPermissions?.canEdit);

  const hasPageEntityAccess = allProfilesAccesses?.some(profileAccess =>
    [ENTITY_TYPES.PROFILE, ENTITY_TYPES.AGENCY, ENTITY_TYPES.ORGANIZATION].some(
      key => pageEntity?.id && profileAccess?.[key]?.id === pageEntity?.id,
    ),
  );

  const hasActiveSubscription = useMemo(
    () => userSubscriptions?.some(subscription => subscription?.status === SUBSCRIPTIONS_STATUSES.active),
    [userSubscriptions],
  );

  // NOTE: A specific paywall variant (e.g., hard_wall/soft_wall) is active
  const isPaywallVariantActive = !!paywallType && (!hasEditPermission || isPaywallExpanded);

  // NOTE: Subscription plans should be shown (e.g., prompt user to choose/upgrade)
  const shouldShowSubscriptionPlans = showSubscriptionPlans?.show;

  const skipStickyFooter =
    !isPaywallVariantActive && !shouldShowSubscriptionPlans && (hasPageEntityAccess || hasActiveSubscription);

  // NOTE: Pages exempt from paywall — login, register, payment, plans — or edit mode without plans — or has access to profile/organization page
  const isPageExemptFromPaywall =
    [
      ROUTE_RESERVED_KEYWORDS.login,
      ROUTE_RESERVED_KEYWORDS.register,
      ROUTE_RESERVED_KEYWORDS.payment,
      ROUTE_RESERVED_KEYWORDS.plans,
      ROUTE_RESERVED_KEYWORDS.redirect,
    ].some(route => pathname.includes(route)) ||
    (edit && !showSubscriptionPlans?.show);

  // NOTE: Trigger paywall only if not on exempt page, and one or more paywall reasons apply
  const shouldTriggerPaywall =
    isLoginDialog?.isOpen ||
    (!isPageExemptFromPaywall && (isPaywallVariantActive || shouldShowSubscriptionPlans) && !skipStickyFooter);

  useEffect(() => {
    if (isLoginDialog?.isOpen) {
      setIsPaywallExpanded(true);
    }
  }, [isLoginDialog?.isOpen, setIsPaywallExpanded]);

  useEffect(() => {
    if (isReturningGuestUser && !isPageExemptFromPaywall) {
      setIsPaywallExpanded(true);
    }
  }, [isReturningGuestUser, setIsPaywallExpanded, isPageExemptFromPaywall]);

  useEffect(() => {
    const relevantEventType = isLoggedIn ? PAYWALL_EVENT_TYPES.AUTHENTICATED : PAYWALL_EVENT_TYPES.GUEST;
    const shouldExpand =
      paywall?.action === PAYWALL_ACTIONS.SHOW &&
      !!paywallType &&
      !hasEditPermission &&
      paywall?.paywallMeta?.eventType === relevantEventType;

    if (shouldExpand) {
      setIsPaywallExpanded(true);
    }
  }, [
    isLoggedIn,
    paywallType,
    paywall?.action,
    paywall?.paywallMeta?.eventType,
    hasEditPermission,
    setIsPaywallExpanded,
  ]);

  useEffect(() => {
    if (paywall?.refresh?.permissions && typeof refetchPermissions === 'function') {
      refetchPermissions();
    }
  }, [paywall?.refresh?.permissions, refetchPermissions]);

  useEffect(() => {
    if (isReturningGuestUser && !isPageExemptFromPaywall) {
      setIsLoginDialog({ source: LOGIN_SOURCE.RETURNING_USER });
      track.actionCallback({
        section: SECTIONS.RETURNING_USER,
        component: COMPONENTS.MODAL,
        meta: {
          step: ACTION_CALLBACK_STEPS.VIEW_RETURNING_USER_MODAL,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
          payload: {
            userEmail: visitorIdentity?.email,
            loginType: visitorIdentity?.loginType,
          },
        },
      });
    }
  }, [track, isReturningGuestUser, visitorIdentity, setIsLoginDialog, isPageExemptFromPaywall]);

  useEffect(() => {
    const withPermissionCheck = isPermissionsReady && !isAdmin;
    const hasRelevantUserDetails = has(userData, 'emailVerified') && has(userData, 'profiles');

    if (isLoggedIn && hasRelevantUserDetails && !!userData?.id && withPermissionCheck) {
      const hasIncompleteName = !userData.firstName?.trim() || !userData.lastName?.trim();
      const hasNoOwnerProfile = !userData?.profiles?.length && accessPendingProfileIds?.length === 0;
      const hasNoEmailVerified = !userData.emailVerified;

      if (hasIncompleteName || hasNoOwnerProfile || hasNoEmailVerified) {
        setIsLoginDialog({
          isOpen: true,
          step: STEP_VALUES[STEPS_LOGIN.REGISTRATION_UPDATE],
          source: LOGIN_SOURCE.MANDATORY_NAME,
        });
      }
    }
  }, [isLoggedIn, setIsLoginDialog, userData, isPermissionsReady, isAdmin, accessPendingProfileIds]);

  useEffect(() => {
    const paymentQueryParams = pick(queryParams, ['payment_status', 'payment_origin']);

    if (!Object.keys(paymentQueryParams).length) {
      return;
    }

    const trackPayload = {
      section: SECTIONS.PAYMENT_CALLBACK,
      meta: {
        type: paymentQueryParams?.payment_origin,
        step: paywallType,
        ...(!!paymentQueryParams?.payment_status && {
          actionStatus:
            paymentQueryParams?.payment_status === PAYMENT_CALLBACK_STATUSES.SUCCESS
              ? ACTION_CALLBACK_STATUS.SUCCESS
              : ACTION_CALLBACK_STATUS.FAILURE,
        }),
      },
    };
    navigate.updateQueryParams({ deleteParams: Object.keys(paymentQueryParams) });
    track.actionCallback(trackPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldTriggerPaywall && isLoggedIn && paywallType && paywall?.action === PAYWALL_ACTIONS.SHOW) {
      track.actionCallback({
        section: SECTIONS.PAYWALL,
        component: COMPONENTS.MODAL,
        meta: {
          step: paywallType,
          isLoggedIn,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
    }
  }, [paywallType, paywall?.action, track, shouldTriggerPaywall, isLoggedIn]);

  useEffect(() => {
    if (shouldTriggerPaywall && !isLoggedIn && isPaywallExpanded) {
      track.actionCallback({
        section: SECTIONS.LOGIN_WALL,
        component: COMPONENTS.MODAL,
        meta: {
          step: paywallType,
          isLoggedIn,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
    }
  }, [paywallType, track, shouldTriggerPaywall, isLoggedIn, isPaywallExpanded]);

  const handleSetShowSubscriptionPlans = useCallback(
    ({ show = false, entityType, entity, subscription, identifier } = {}) => {
      if (show) {
        const entitySubscription = subscription || entity?.subscription || null;

        const invoiceDueDate = entitySubscription?.invoice?.due_date;
        const invoiceStatus = entitySubscription?.invoice?.status;
        const isDue = invoiceDueDate && createDate(invoiceDueDate * 1000).isSameOrAfter(createDate(), 'minute');
        const isOverdue = invoiceDueDate && createDate(invoiceDueDate * 1000).isBefore(createDate(), 'minute');
        const isUnpaid = invoiceStatus !== SUBSCRIPTION_PAYMENT_STATUSES.PAID;
        const isPaymentDue = isUnpaid && isDue;
        const isPaymentOverdue = isUnpaid && isOverdue;

        if (isPaymentDue || isPaymentOverdue) {
          setPaymentOptionsModal({
            isOpen: true,
            entity: {
              ...entity,
              subscription: entitySubscription,
            },
            entityType,
          });
        } else {
          setShowSubscriptionPlans({
            show,
            entityType,
            entity,
            identifier,
            subscription: subscription || entity?.subscription || null,
          });
        }
      } else {
        setShowSubscriptionPlans({ show: false, entityType: null, entity: null, subscription: null, identifier: null });
      }
    },
    [setShowSubscriptionPlans, setPaymentOptionsModal],
  );

  const value = useMemo(
    () => ({
      showSubscriptionPlans,
      setShowSubscriptionPlans: handleSetShowSubscriptionPlans,
      isPaywallExpanded,
      setIsPaywallExpanded,
      isReturningGuestUser,
    }),
    [
      showSubscriptionPlans,
      handleSetShowSubscriptionPlans,
      isPaywallExpanded,
      isReturningGuestUser,
      setIsPaywallExpanded,
    ],
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {shouldTriggerPaywall && (
        <PaywallContent
          showSubscriptionPlans={!isLoginDialog?.isOpen && showSubscriptionPlans}
          setShowSubscriptionPlans={handleSetShowSubscriptionPlans}
          isPaywallExpanded={isPaywallExpanded}
          setIsPaywallExpanded={setIsPaywallExpanded}
        />
      )}
    </PaywallContext.Provider>
  );
};

const usePaywallContext = () => useContext(PaywallContext);

export { usePaywallContext };
export default Paywall;
